import React, { useEffect, useMemo, useState } from "react";

/**
 * Teletorget Admin – Blog Post Builder (compact)
 * - Blog cards like frontend
 * - Compose blocks + upload media
 * - "Les" opens full-size preview modal
 * - Close modal with ESC / backdrop / button
 */

type ActiveTab = "Compose" | "Preview";

type PostTemplate = "Festival" | "Guide" | "Velstand" | "Standard";

type ContentBlock =
  | { id: string; type: "heading"; text: string }
  | { id: string; type: "paragraph"; text: string }
  | { id: string; type: "quote"; text: string; by?: string }
  | { id: string; type: "image"; url?: string; caption?: string }
  | { id: string; type: "video"; url?: string; caption?: string };

type DraftPost = {
  title: string;
  subtitle: string;
  template: PostTemplate;
  readTimeMin: number;
  status: "Draft" | "Scheduled" | "Published";
  publishAt?: string;
  cover?: { name: string; url: string };
  gallery: { name: string; url: string }[];
  video?: { name: string; url: string; kind: "upload" | "embed" };
  blocks: ContentBlock[];
};

type PreviewPost = {
  id: string;
  template: PostTemplate;
  title: string;
  subtitle: string;
  readTimeMin: number;
  imageUrl?: string;
  blocks?: ContentBlock[];
};

const uid = () => Math.random().toString(16).slice(2) + Date.now().toString(16);
const cn = (...c: Array<string | false | undefined | null>) => c.filter(Boolean).join(" ");

function isTemplate(v: string): v is PostTemplate {
  return v === "Festival" || v === "Guide" || v === "Velstand" || v === "Standard";
}

function isStatus(v: string): v is DraftPost["status"] {
  return v === "Draft" || v === "Scheduled" || v === "Published";
}

function moveInArray<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr;
  if (from < 0 || from >= arr.length) return arr;
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

function updateBlock(blocks: ContentBlock[], id: string, patch: Partial<ContentBlock>): ContentBlock[] {
  return blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as ContentBlock) : b));
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700">{label}</div>
      {children}
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cn(
        "mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400",
        "focus:outline-none focus:ring-2 focus:ring-violet-300",
        props.className
      )}
    />
  );
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={cn(
        "mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400",
        "focus:outline-none focus:ring-2 focus:ring-violet-300",
        props.className
      )}
    />
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-300"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function FilePicker({
  label,
  accept,
  multiple,
  onPicked,
}: {
  label: string;
  accept?: string;
  multiple?: boolean;
  onPicked: (files: File[]) => void;
}) {
  return (
    <label className="mt-2 flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 hover:bg-slate-50">
      <span className="truncate">{label}</span>
      <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white">Velg</span>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          const list = Array.from(e.target.files ?? []);
          if (!list.length) return;
          onPicked(list);
          e.currentTarget.value = "";
        }}
      />
    </label>
  );
}

function Badge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
      <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
      {label}
    </span>
  );
}

function BlogCard({
  title,
  subtitle,
  template,
  readTime,
  imageUrl,
  onRead,
}: {
  title: string;
  subtitle: string;
  template: string;
  readTime: number;
  imageUrl?: string;
  onRead?: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
      <div className="h-44 bg-slate-100">
        {imageUrl ? <img src={imageUrl} alt="" className="h-full w-full object-cover" /> : null}
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between gap-3">
          <Badge label={template === "Standard" ? "Innlegg" : template} />
          <span className="text-xs text-slate-500">{readTime} min</span>
        </div>
        <div className="mt-4 text-lg font-semibold text-slate-900">{title}</div>
        <div className="mt-2 text-sm text-slate-600">{subtitle}</div>
        <div className="mt-5">
          <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-2xl px-5" onClick={onRead}>
            Les
          </button>
        </div>
      </div>
    </div>
  );
}

function BlockRenderer({ blocks }: { blocks: ContentBlock[] }) {
  return (
    <div className="space-y-6">
      {blocks.map((b) => {
        if (b.type === "heading") {
          return (
            <div key={b.id} className="text-2xl font-semibold text-slate-900">
              {b.text}
            </div>
          );
        }
        if (b.type === "paragraph") {
          return (
            <div key={b.id} className="text-slate-700 leading-relaxed whitespace-pre-line">
              {b.text}
            </div>
          );
        }
        if (b.type === "quote") {
          return (
            <div key={b.id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-slate-900 font-semibold">“{b.text}”</div>
              {b.by ? <div className="mt-2 text-sm text-slate-500">— {b.by}</div> : null}
            </div>
          );
        }
        if (b.type === "image") {
          if (!b.url) return null;
          return (
            <div key={b.id}>
              <img src={b.url} alt="" className="h-[360px] w-full rounded-3xl border border-slate-200 object-cover" />
              {b.caption ? <div className="mt-2 text-sm text-slate-500">{b.caption}</div> : null}
            </div>
          );
        }
        if (b.type === "video") {
          return (
            <div key={b.id} className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white/80">
              <div className="text-sm font-semibold">Video</div>
              <div className="mt-2 text-sm break-all">{b.url || "(ingen URL)"}</div>
              {b.caption ? <div className="mt-2 text-sm text-white/60">{b.caption}</div> : null}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function ArticleModal({ post, onClose }: { post: PreviewPost; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />

      <div className="relative mx-auto h-[92vh] max-w-5xl px-4 pt-6">
        <div className="h-full overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_80px_rgba(15,23,42,0.35)]">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-4">
            <div className="flex items-center gap-3">
              <Badge label={post.template === "Standard" ? "Innlegg" : post.template} />
              <div className="text-xs text-slate-500">{post.readTimeMin} min</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:block text-xs text-slate-500">ESC for å lukke</div>
              <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={onClose}>
                Lukk
              </button>
            </div>
          </div>

          <div className="h-[calc(92vh-64px)] overflow-auto">
            {post.imageUrl ? <img src={post.imageUrl} alt="" className="h-[340px] w-full object-cover" /> : null}

            <div className="px-6 py-8 sm:px-10">
              <div className="text-3xl font-semibold text-slate-900">{post.title}</div>
              <div className="mt-3 text-slate-600">{post.subtitle}</div>

              <div className="mt-8">
                {post.blocks && post.blocks.length ? (
                  <BlockRenderer blocks={post.blocks} />
                ) : (
                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 text-slate-700">
                    Dette innlegget har ikke blokk-data i demoen. I produksjon kommer blocks fra Teletorget CMS API.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BlogDetail() {
  const [tab, setTab] = useState<ActiveTab>("Compose");
  const [toast, setToast] = useState<string | null>(null);
  const [openPost, setOpenPost] = useState<PreviewPost | null>(null);

  const [draft, setDraft] = useState<DraftPost>(() => ({
    title: "Navratri: 9 netter for fornyelse",
    subtitle: "Ritualer, farger og intensjoner – én for hver Navratri-dag.",
    template: "Festival",
    readTimeMin: 3,
    status: "Draft",
    publishAt: new Date().toISOString().slice(0, 16),
    cover: undefined,
    gallery: [],
    video: undefined,
    blocks: [
      { id: uid(), type: "heading", text: "Hva er Navratri?" },
      {
        id: uid(),
        type: "paragraph",
        text: "Skriv en kort og konkret introduksjon som fungerer både i feed og i artikkel.",
      },
      { id: uid(), type: "quote", text: "Konsistens slår motivasjon.", by: "Teletorget Editorial" },
    ],
  }));

  const [existingPosts] = useState<PreviewPost[]>(() => [
    {
      id: uid(),
      template: "Guide",
      title: "Slik velger du veileder",
      subtitle: "Klarsynt, Tarot eller Astrologi? Finn rett retning for spørsmålet ditt.",
      readTimeMin: 3,
      imageUrl: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1400&q=80",
      blocks: [
        { id: uid(), type: "heading", text: "Rask sjekkliste" },
        { id: uid(), type: "paragraph", text: "• Definer målet\n• Velg format\n• Sett forventninger" },
      ],
    },
    {
      id: uid(),
      template: "Velstand",
      title: "Diwali-ritualer for velstand",
      subtitle: "Enkel Lakshmi-puja og moderne praksiser du kan gjøre hjemme.",
      readTimeMin: 3,
      imageUrl: "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1400&q=80",
      blocks: [
        { id: uid(), type: "paragraph", text: "Dette er en demo-tekst for preview. I produksjon hentes innholdet fra API." },
        { id: uid(), type: "quote", text: "Små ritualer, stor effekt.", by: "Redaksjonen" },
      ],
    },
  ]);

  const coverPreview = useMemo(() => draft.cover?.url, [draft.cover?.url]);
  const canPublish = draft.title.trim().length >= 4 && draft.subtitle.trim().length >= 10;

  const notify = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  };

  const attach = (files: File[]) => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));

  const addBlock = (type: ContentBlock["type"]) => {
    const b: ContentBlock =
      type === "heading"
        ? { id: uid(), type, text: "Ny seksjon" }
        : type === "paragraph"
          ? { id: uid(), type, text: "Skriv inn tekst..." }
          : type === "quote"
            ? { id: uid(), type, text: "Sitat...", by: "" }
            : type === "image"
              ? { id: uid(), type, url: "", caption: "" }
              : { id: uid(), type: "video", url: "", caption: "" };
    setDraft((d) => ({ ...d, blocks: [...d.blocks, b] }));
  };

  const removeBlock = (id: string) => setDraft((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== id) }));

  const moveBlock = (id: string, dir: -1 | 1) =>
    setDraft((d) => {
      const idx = d.blocks.findIndex((b) => b.id === id);
      if (idx < 0) return d;
      return { ...d, blocks: moveInArray(d.blocks, idx, idx + dir) };
    });

  const setBlock = (id: string, patch: Partial<ContentBlock>) =>
    setDraft((d) => ({ ...d, blocks: updateBlock(d.blocks, id, patch) }));

  const saveDraft = () => notify("Kladd lagret (demo)");
  const schedule = () => (canPublish ? (setDraft((d) => ({ ...d, status: "Scheduled" })), notify("Planlagt (demo)")) : null);
  const publish = () => (canPublish ? (setDraft((d) => ({ ...d, status: "Published" })), notify("Publisert (demo)")) : null);

  const statusLabel = draft.status === "Draft" ? "Kladd" : draft.status === "Scheduled" ? "Planlagt" : "Publisert";

  const openDraftPreview = () =>
    setOpenPost({
      id: "draft",
      template: draft.template,
      title: draft.title || "(Uten tittel)",
      subtitle: draft.subtitle || "(Uten ingress)",
      readTimeMin: draft.readTimeMin,
      imageUrl: draft.cover?.url,
      blocks: draft.blocks,
    });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Blogg – Nytt innlegg</div>
              <div className="text-xs text-slate-500">Teletorget backend • CMS & innhold</div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1">
              <button
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-semibold",
                  tab === "Compose" ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-100"
                )}
                onClick={() => setTab("Compose")}
              >
                Rediger
              </button>
              <button
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-semibold",
                  tab === "Preview" ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-100"
                )}
                onClick={() => setTab("Preview")}
              >
                Forhåndsvis
              </button>
            </div>
            <button className="rounded-full px-4 py-2 border text-sm" onClick={saveDraft}>
              Lagre
            </button>
            <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed bg-slate-900 text-white hover:bg-slate-800" onClick={schedule} disabled={!canPublish}>
              Planlegg
            </button>
            <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed bg-violet-600 text-white hover:bg-violet-700" onClick={publish} disabled={!canPublish}>
              Publiser
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 py-6 lg:grid-cols-12">
        {/* LEFT – Compose */}
        <div className={cn("lg:col-span-7", tab === "Preview" ? "hidden lg:block" : "block")}>
          <div className="space-y-6">
            {/* Metadata */}
            <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Metadata</div>
                  <div className="mt-1 text-xs text-slate-500">Tittel, template, lesetid, status og publisering</div>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{statusLabel}</span>
              </div>
              <div className="px-6 py-5">
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <Field label="Tittel" hint="Skal fungere i kortvisning og i SEO.">
                      <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Ingress / kort beskrivelse">
                      <Textarea rows={3} value={draft.subtitle} onChange={(e) => setDraft((d) => ({ ...d, subtitle: e.target.value }))} />
                    </Field>
                  </div>
                  <Field label="Oppsett (template)" hint="Driver badge og standardoppsett.">
                    <Select value={draft.template} onChange={(v) => isTemplate(v) && setDraft((d) => ({ ...d, template: v }))} options={["Festival", "Guide", "Velstand", "Standard"]} />
                  </Field>
                  <Field label="Lesetid (min)">
                    <Input type="number" min={1} value={draft.readTimeMin} onChange={(e) => setDraft((d) => ({ ...d, readTimeMin: Math.max(1, Number(e.target.value || 1)) }))} />
                  </Field>
                  <Field label="Publiseringsdato" hint="Brukes ved planlagt publisering.">
                    <Input type="datetime-local" value={draft.publishAt ?? ""} onChange={(e) => setDraft((d) => ({ ...d, publishAt: e.target.value }))} />
                  </Field>
                  <Field label="Status">
                    <Select value={draft.status} onChange={(v) => isStatus(v) && setDraft((d) => ({ ...d, status: v }))} options={["Draft", "Scheduled", "Published"]} />
                  </Field>
                </div>
              </div>
            </div>

            {/* Media */}
            <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <div className="border-b border-slate-100 px-6 py-5">
                <div className="text-sm font-semibold text-slate-900">Media</div>
                <div className="mt-1 text-xs text-slate-500">Cover, galleri og video (upload eller embed)</div>
              </div>
              <div className="px-6 py-5">
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <Field label="Coverbilde" hint="Vises på bloggkortet.">
                      <FilePicker
                        label={draft.cover ? draft.cover.name : "Last opp coverbilde"}
                        accept="image/*"
                        onPicked={(files) => {
                          const [f] = files;
                          const [obj] = attach([f]);
                          setDraft((d) => ({ ...d, cover: obj }));
                          notify("Cover lagt til");
                        }}
                      />
                      {coverPreview ? <img src={coverPreview} alt="" className="mt-3 h-36 w-full rounded-3xl border border-slate-200 object-cover" /> : null}
                    </Field>
                  </div>

                  <div>
                    <Field label="Galleri (valgfritt)" hint="For tidsriktige oppsett (steg/karusell).">
                      <FilePicker
                        label={draft.gallery.length ? `${draft.gallery.length} filer valgt` : "Legg til galleri"}
                        accept="image/*"
                        multiple
                        onPicked={(files) => {
                          const urls = attach(files);
                          setDraft((d) => ({ ...d, gallery: [...d.gallery, ...urls] }));
                          notify("Galleri oppdatert");
                        }}
                      />
                      {draft.gallery.length ? (
                        <div className="mt-3 grid grid-cols-3 gap-2">
                          {draft.gallery.slice(0, 6).map((g) => (
                            <div key={g.url} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                              <img src={g.url} alt="" className="h-16 w-full object-cover" />
                              <button className="absolute right-2 top-2 rounded-full bg-white/90 px-2 py-1 text-xs font-semibold" onClick={() => setDraft((d) => ({ ...d, gallery: d.gallery.filter((x) => x.url !== g.url) }))}>
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </Field>
                  </div>

                  <div className="md:col-span-2 rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Video</div>
                        <div className="mt-1 text-xs text-slate-500">Upload MP4/WebM eller embed URL.</div>
                      </div>
                      <div className="flex gap-2">
                        <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => setDraft((d) => ({ ...d, video: { name: "Embed", url: d.video?.url ?? "", kind: "embed" } }))}>
                          Embed
                        </button>
                        <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => setDraft((d) => ({ ...d, video: { name: d.video?.name ?? "", url: d.video?.url ?? "", kind: "upload" } }))}>
                          Upload
                        </button>
                      </div>
                    </div>

                    {draft.video?.kind === "upload" ? (
                      <div className="mt-3">
                        <FilePicker
                          label={draft.video?.name ? draft.video.name : "Last opp video"}
                          accept="video/*"
                          onPicked={(files) => {
                            const [f] = files;
                            const [obj] = attach([f]);
                            setDraft((d) => ({ ...d, video: { ...obj, kind: "upload" } }));
                            notify("Video lagt til");
                          }}
                        />
                        {draft.video?.url ? (
                          <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-black">
                            <video controls className="h-56 w-full">
                              <source src={draft.video.url} />
                            </video>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {draft.video?.kind === "embed" ? (
                      <div className="mt-3">
                        <Field label="Embed URL">
                          <Input value={draft.video?.url ?? ""} onChange={(e) => setDraft((d) => ({ ...d, video: { name: "Embed", url: e.target.value, kind: "embed" } }))} placeholder="https://www.youtube.com/watch?v=..." />
                        </Field>
                      </div>
                    ) : null}

                    {draft.video ? (
                      <div className="mt-3 flex justify-end">
                        <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => setDraft((d) => ({ ...d, video: undefined }))}>
                          Fjern video
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Content blocks */}
            <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Innhold</div>
                  <div className="mt-1 text-xs text-slate-500">Blokker: heading, tekst, sitat, bilde, video</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => addBlock("heading")}>H</button>
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => addBlock("paragraph")}>Tekst</button>
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => addBlock("quote")}>Sitat</button>
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => addBlock("image")}>Bilde</button>
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" onClick={() => addBlock("video")}>Video</button>
                </div>
              </div>

              <div className="px-6 py-5">
                <div className="space-y-4">
                  {draft.blocks.map((b) => (
                    <div key={b.id} className="rounded-3xl border border-slate-200 bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{b.type.toUpperCase()}</div>
                          <div className="mt-1 text-xs text-slate-500">Modulær struktur (micro-component mindset)</div>
                        </div>
                        <div className="flex gap-2">
                          <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 px-3 py-1" onClick={() => moveBlock(b.id, -1)}>↑</button>
                          <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 px-3 py-1" onClick={() => moveBlock(b.id, 1)}>↓</button>
                          <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed bg-rose-600 text-white hover:bg-rose-700 px-3 py-1" onClick={() => removeBlock(b.id)}>Slett</button>
                        </div>
                      </div>

                      <div className="mt-4">
                        {b.type === "heading" || b.type === "paragraph" ? (
                          <Textarea rows={b.type === "heading" ? 2 : 4} value={(b as any).text} onChange={(e) => setBlock(b.id, { text: e.target.value })} />
                        ) : null}

                        {b.type === "quote" ? (
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="md:col-span-2">
                              <Field label="Sitat">
                                <Textarea rows={3} value={(b as any).text} onChange={(e) => setBlock(b.id, { text: e.target.value })} />
                              </Field>
                            </div>
                            <Field label="Avsender (valgfritt)">
                              <Input value={(b as any).by ?? ""} onChange={(e) => setBlock(b.id, { by: e.target.value })} />
                            </Field>
                          </div>
                        ) : null}

                        {b.type === "image" ? (
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="Bilde URL">
                              <Input value={(b as any).url ?? ""} onChange={(e) => setBlock(b.id, { url: e.target.value })} placeholder="https://..." />
                            </Field>
                            <Field label="Caption (valgfritt)">
                              <Input value={(b as any).caption ?? ""} onChange={(e) => setBlock(b.id, { caption: e.target.value })} />
                            </Field>
                            {(b as any).url ? <img src={(b as any).url} alt="" className="md:col-span-2 h-60 w-full rounded-3xl border border-slate-200 object-cover" /> : null}
                          </div>
                        ) : null}

                        {b.type === "video" ? (
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field label="Video URL">
                              <Input value={(b as any).url ?? ""} onChange={(e) => setBlock(b.id, { url: e.target.value })} placeholder="https://..." />
                            </Field>
                            <Field label="Caption (valgfritt)">
                              <Input value={(b as any).caption ?? ""} onChange={(e) => setBlock(b.id, { caption: e.target.value })} />
                            </Field>
                            {(b as any).url ? <div className="md:col-span-2 flex h-56 items-center justify-center rounded-3xl border border-slate-200 bg-slate-900 text-sm text-white/70">Video preview (frontend renderer)</div> : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}

                  <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Preview artikkel</div>
                        <div className="mt-1 text-xs text-slate-500">Klikk for fullskjerms preview. ESC/lukk lukker modal.</div>
                      </div>
                      <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed bg-slate-900 text-white hover:bg-slate-800" onClick={openDraftPreview}>
                        Åpne preview
                      </button>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-900">Hurtigoppsett</div>
                        <div className="mt-1 text-xs text-slate-500">Legg til en standard struktur.</div>
                      </div>
                      <button
                        className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        onClick={() => {
                          const base: ContentBlock[] = [
                            { id: uid(), type: "heading", text: "Kort oppsummering" },
                            { id: uid(), type: "paragraph", text: "1–2 avsnitt som gir verdi raskt." },
                            { id: uid(), type: "image", url: draft.cover?.url ?? "", caption: "" },
                            { id: uid(), type: "heading", text: "Steg-for-steg" },
                            { id: uid(), type: "paragraph", text: "• Steg 1\n• Steg 2\n• Steg 3" },
                          ];
                          setDraft((d) => ({ ...d, blocks: [...d.blocks, ...base] }));
                          notify("Oppsett lagt til");
                        }}
                      >
                        Legg til
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      onClick={() => {
                        setDraft((d) => ({
                          ...d,
                          blocks: [
                            { id: uid(), type: "heading", text: d.title || "Ny artikkel" },
                            { id: uid(), type: "paragraph", text: d.subtitle || "Ingress" },
                            { id: uid(), type: "quote", text: "Nøkkelpoeng som skaper retning og fokus.", by: "" },
                          ],
                        }));
                        notify("Nullstilt");
                      }}
                    >
                      Nullstill blokker
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT – Preview */}
        <div className={cn("lg:col-span-5", tab === "Compose" ? "hidden lg:block" : "block")}>
          <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div>
                <div className="text-sm font-semibold text-slate-900">Live forhåndsvisning</div>
                <div className="mt-1 text-xs text-slate-500">Blogg-grid (kortvisning) slik brukeren ser det</div>
              </div>
              <Badge label="Frontend preview" />
            </div>
            <div className="px-6 py-5">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between">
                  <div className="text-xl font-semibold text-slate-900">Fra Mystic-bloggen</div>
                  <button className="inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-50 disabled:cursor-not-allowed bg-violet-600 text-white hover:bg-violet-700 rounded-2xl px-5">
                    Se alle
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                  <BlogCard
                    title={draft.title || "(Uten tittel)"}
                    subtitle={draft.subtitle || "(Uten ingress)"}
                    template={draft.template}
                    readTime={draft.readTimeMin}
                    imageUrl={draft.cover?.url}
                    onRead={openDraftPreview}
                  />
                  {existingPosts.map((p) => (
                    <BlogCard
                      key={p.id}
                      title={p.title}
                      subtitle={p.subtitle}
                      template={p.template}
                      readTime={p.readTimeMin}
                      imageUrl={p.imageUrl}
                      onRead={() => setOpenPost(p)}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Status</span>
                  <span className="text-slate-500">{statusLabel}</span>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  API mapping: POST /api/blog/posts • POST /api/media/upload • GET /api/blog/posts?status=...&limit=...
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
          <div className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg">{toast}</div>
        </div>
      ) : null}

      {/* Full-size modal preview */}
      {openPost ? <ArticleModal post={openPost} onClose={() => setOpenPost(null)} /> : null}

      {/* Mobile tab switch */}
      <div className="fixed bottom-4 left-1/2 z-30 w-[92%] max-w-md -translate-x-1/2 sm:hidden">
        <div className="flex items-center justify-between rounded-full border border-slate-200 bg-white p-1 shadow-lg">
          <button
            className={cn(
              "w-full rounded-full px-4 py-2 text-sm font-semibold",
              tab === "Compose" ? "bg-violet-600 text-white" : "text-slate-700"
            )}
            onClick={() => setTab("Compose")}
          >
            Rediger
          </button>
          <button
            className={cn(
              "w-full rounded-full px-4 py-2 text-sm font-semibold",
              tab === "Preview" ? "bg-violet-600 text-white" : "text-slate-700"
            )}
            onClick={() => setTab("Preview")}
          >
            Forhåndsvis
          </button>
        </div>
      </div>
    </div>
  );
}
